import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { debugAuthResponse, debugUserState } from '../utils/debug-auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Setup axios interceptor for auth token and tenant code
  useEffect(() => {
    // Set base URL for axios
    // Default to empty string (relative URLs) for production, or set REACT_APP_API_URL
    const API_BASE_URL = getApiBaseUrl();
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH) {
      console.log('🔍 AUTH_DEBUG: API_BASE_URL:', API_BASE_URL);
    }
    axios.defaults.baseURL = API_BASE_URL;
    
    // Add request interceptor to automatically include X-Tenant-Code header
    // This interceptor reads from localStorage on each request, so it's always up-to-date
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Get tenant code from localStorage first (most up-to-date, set when tenant is selected)
        // Priority: selectedTenantCode (string) > selectedTenant (object) > JWT token
        let tenantCode = localStorage.getItem('selectedTenantCode');
        
        // If selectedTenantCode is not set, try selectedTenant (full object)
        if (!tenantCode) {
          const selectedTenant = localStorage.getItem('selectedTenant');
          if (selectedTenant) {
            try {
              const tenant = JSON.parse(selectedTenant);
              tenantCode = tenant.repositoryCode || tenant.tenant_code || tenant.code;
            } catch (e) {
              // If parsing fails, treat as string
              tenantCode = selectedTenant;
            }
          }
        }
        
        // Fallback to JWT token if localStorage doesn't have it
        if (!tenantCode) {
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              tenantCode = payload.tenantCode;
            } catch (e) {
              // Token decode failed
            }
          }
        }
        
        // Add X-Tenant-Code header if we have a tenant code and it's not DEFAULT
        if (tenantCode && tenantCode !== 'DEFAULT') {
          config.headers['X-Tenant-Code'] = tenantCode;
        } else if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH && config.url && !config.url.includes('/auth/login') && !config.url.includes('/auth/register')) {
          console.warn('🔍 AXIOS_INTERCEPTOR: No tenant code for URL:', config.url);
        }
        const selectedRole = localStorage.getItem('selectedRole');
        if (selectedRole && config.url && !config.url.includes('/auth/login') && !config.url.includes('/auth/register')) {
          config.headers['X-Selected-Role'] = selectedRole;
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      // Verify token validity
      verifyToken();
    } else {
      setLoading(false);
    }
    
    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []); // Empty dependency array - set up once on mount

  // Debounce token verification to prevent excessive API calls
  let verifyTokenTimeout = null;
  const verifyToken = async (skipDelay = false) => {
    // Clear any pending verification
    if (verifyTokenTimeout) {
      clearTimeout(verifyTokenTimeout);
      verifyTokenTimeout = null;
    }

    const performVerification = async () => {
      try {
        const response = await axios.get('/api/auth/profile');
        const profileUser = response.data.user;
        
        // Debug logging to see what we're getting from the profile endpoint
        console.log('🔍 PROFILE_DEBUG: Full profile response:', profileUser);
        console.log('🔍 PROFILE_DEBUG: Role information:', {
          role: profileUser.role,
          roles: profileUser.roles,
          userType: profileUser.userType,
          isAdmin: profileUser.isAdmin,
          isSuperAdmin: profileUser.isSuperAdmin
        });
        console.log('🔍 PROFILE_DEBUG: Tenant information:', {
          tenants: profileUser.tenants,
          tenantCount: profileUser.tenants?.length,
          tenantCode: profileUser.tenantCode
        });
        
        // Restore selected role from localStorage if available
        const savedRole = localStorage.getItem('selectedRole');
        if (savedRole && profileUser.roles) {
          const hasRole = profileUser.roles.some(r => {
            const rName = r.RoleName || r;
            return rName === savedRole;
          });
          if (hasRole) {
            profileUser.selectedRole = { RoleName: savedRole };
            console.log('🔍 AUTH_CONTEXT: Restored selected role from localStorage:', savedRole);
          } else {
            // Remove invalid saved role
            localStorage.removeItem('selectedRole');
            console.log('🔍 AUTH_CONTEXT: Removed invalid saved role:', savedRole);
          }
        }
        
        setUser(profileUser);
        // Try to ensure tenant context after loading profile
        await ensureTenantContext(profileUser);
      } catch (error) {
        console.error('Token verification failed:', error);
        // Token is invalid
        localStorage.removeItem('token');
        localStorage.removeItem('selectedTenantCode');
        localStorage.removeItem('selectedTenant');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    if (skipDelay) {
      await performVerification();
    } else {
      // Debounce by 300ms to avoid multiple rapid calls
      verifyTokenTimeout = setTimeout(performVerification, 300);
    }
  };

  const login = async (credentials) => {
    try {
      console.log('🔍 LOGIN_DEBUG: Attempting login with credentials:', credentials);
      console.log('🔍 LOGIN_DEBUG: Making API call to:', '/api/auth/login');
      console.log('🔍 LOGIN_DEBUG: Axios baseURL:', axios.defaults.baseURL);
      
      // Clear any stale auth data before login attempt
      delete axios.defaults.headers.common['X-Tenant-Code'];
      
      const response = await axios.post('/api/auth/login', credentials);
      
      // Debug logging
      debugAuthResponse(response.data);
      console.log('🔍 LOGIN_DEBUG: Full login response:', response.data);
      console.log('🔍 LOGIN_DEBUG: User object from login:', response.data.user);
      console.log('🔍 LOGIN_DEBUG: Role information from login:', {
        role: response.data.user?.role,
        roles: response.data.user?.roles,
        userType: response.data.userType,
        isAdmin: response.data.user?.isAdmin,
        isSuperAdmin: response.data.user?.isSuperAdmin
      });
      
      const { token: newToken, user, userType, requiresTenantSelection, tenants, defaultTenant, noTenantAccess, message } = response.data;
      
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      
      // Set user with additional properties from dual auth system
      const userData = {
        ...user,
        userType,
        tenants,
        requiresTenantSelection,
        defaultTenant,
        noTenantAccess
      };
      
      // Debug logging for user data being set
      debugUserState(userData);
      
      setUser(userData);
      
      // Only refresh profile if role information is missing
      if (!userData.role && !userData.roles) {
        try {
          console.log('🔍 LOGIN_DEBUG: Role information missing, refreshing profile after login...');
          await verifyToken();
        } catch (error) {
          console.error('🔍 LOGIN_DEBUG: Failed to refresh profile after login:', error);
        }
      } else {
        console.log('🔍 LOGIN_DEBUG: Role information present, skipping profile refresh');
      }
      
      if (noTenantAccess) {
        // User has no tenant assignments
        toast.error(message || 'No tenant access assigned. Please contact your administrator.');
        return { 
          success: true, 
          userType,
          requiresTenantSelection: false,
          tenants: [],
          defaultTenant: null,
          noTenantAccess: true,
          message
        };
      } else {
        toast.success(`${userType === 'admin' ? 'Admin' : 'User'} login successful!`);
      }
      
      return { 
        success: true, 
        userType,
        requiresTenantSelection,
        tenants,
        defaultTenant,
        noTenantAccess
      };
    } catch (error) {
      console.log('🔍 LOGIN_DEBUG: Login error:', error);
      console.log('🔍 LOGIN_DEBUG: Error response:', error.response);
      console.log('🔍 LOGIN_DEBUG: Error message:', error.message);
      console.log('🔍 LOGIN_DEBUG: Error config:', error.config);
      
      // Extract detailed error information from the response
      const errorData = error.response?.data || {};
      const message = errorData.error || errorData.message || error.message || 'Login failed';
      const code = errorData.code || null;
      const status = error.response?.status || null;
      
      // Don't show toast for authentication errors - let the Login component handle the display
      // Only show toast for unexpected errors
      if (status !== 401 && status !== 403 && status !== 423) {
        toast.error(message);
      }
      
      // Log rate limit errors specifically
      if (status === 429) {
        console.error('🔍 LOGIN_DEBUG: Rate limit exceeded (429)', {
          message,
          retryAfter: errorData.retryAfter,
          fullError: errorData
        });
      }
      
      return { 
        success: false, 
        error: message,
        code: code,
        status: status,
        details: errorData
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      const { token, user, userType, requiresTenantSelection, tenants, defaultTenant, noTenantAccess } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      
      // Set user with additional properties from registration response
      const userWithTenants = {
        ...user,
        userType,
        tenants,
        requiresTenantSelection,
        defaultTenant,
        noTenantAccess
      };
      
      setUser(userWithTenants);
      
      // Auto-select default tenant if available
      if (defaultTenant && !requiresTenantSelection) {
        await ensureTenantContext(userWithTenants);
      }
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.data?.error) {
        const backendError = error.response.data.error;
        toast.error(backendError);
        return { success: false, error: backendError };
      } else {
        const message = error.response?.data?.message || 'Registration failed';
        toast.error(message);
        return { success: false, error: message };
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await axios.put('/api/auth/change-password', passwordData);
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const hasRole = useCallback((role) => {
    if (!user) return false;
    if (user.userType === 'admin' && role === 'Admin') return true;
    const selectedRole = user.selectedRole?.RoleName || user.selectedRole;
    if (selectedRole) {
      if (Array.isArray(role)) {
        return role.some(r => {
          const rName = typeof r === 'string' ? r : r.RoleName || r;
          return rName === selectedRole;
        });
      }
      return selectedRole === role;
    }
    if (!user?.roles) return false;
    if (Array.isArray(role)) {
      return role.some(r => user.roles.some(userRole => userRole.RoleName === r));
    }
    return user.roles.some(userRole => userRole.RoleName === role);
  }, [user]);

  const hasPermission = (permission) => {
    // Check if user has specific permission or is admin
    return user?.permissions?.includes(permission) || hasRole('Admin');
  };

  const selectRole = async (roleName, options = {}) => {
    const { silent = false } = options;
    try {
      console.log('🔍 AUTH_CONTEXT: selectRole called with:', roleName);
      
      // Validate that user has this role
      const userRoles = user?.roles || [];
      const hasRole = userRoles.some(r => {
        const rName = r.RoleName || r;
        return rName === roleName;
      });
      
      if (!hasRole) {
        const errorMsg = `You do not have the role: ${roleName}`;
        if (!silent) {
          toast.error(errorMsg);
        }
        return { success: false, error: errorMsg };
      }
      
      // Store selected role in localStorage
      localStorage.setItem('selectedRole', roleName);
      
      // Update user object with selected role
      const updatedUser = {
        ...user,
        selectedRole: { RoleName: roleName }
      };
      setUser(updatedUser);
      
      console.log('🔍 AUTH_CONTEXT: Role selected successfully:', roleName);
      
      if (!silent) {
        toast.success(`Switched to ${roleName} role`);
      }
      
      return { success: true, selectedRole: { RoleName: roleName } };
    } catch (error) {
      const message = error.response?.data?.message || 'Role selection failed';
      if (!silent) {
        toast.error(message);
      }
      return { success: false, error: message };
    }
  };

  const selectTenant = useCallback(async (tenantCode, options = {}) => {
    const { silent = false } = options;
    try {
      try {
        window.dispatchEvent(new CustomEvent('tenant:beforeSwitch', { detail: { tenantCode } }));
      } catch (e) {}
      const response = await axios.post('/api/auth/select-tenant', { tenantCode }, { timeout: 10000 });
      const { token: newToken, user: updatedUser, selectedTenant } = response.data;
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      const userData = {
        ...user,
        ...updatedUser,
        userType: 'regular',
        selectedTenant: selectedTenant,
        requiresTenantSelection: false
      };
      setUser(userData);
      if (selectedTenant?.code) {
        localStorage.setItem('selectedTenantCode', selectedTenant.code);
        localStorage.setItem('selectedTenant', JSON.stringify(selectedTenant));
      }
      if (!silent) {
        toast.success(`Switched to ${selectedTenant?.name || selectedTenant?.code} tenant`);
      }
      try {
        window.dispatchEvent(new CustomEvent('tenant:afterSwitch', { detail: { tenantCode: selectedTenant?.code } }));
      } catch (e) {}
      return { success: true, selectedTenant };
    } catch (error) {
      const message = error.code === 'ECONNABORTED' ? 'Tenant selection timed out. Please try again.' : (error.response?.data?.message || 'Tenant selection failed');
      if (!silent) {
        toast.error(message);
      }
      return { success: false, error: message };
    }
  }, [user]);

  const updateAuthData = (authData) => {
    // Update user data after tenant selection or other auth updates
    setUser(prevUser => ({
      ...prevUser,
      ...authData.user,
      selectedTenant: authData.selectedTenant || prevUser?.selectedTenant
    }));
    
    if (authData.token) {
      localStorage.setItem('token', authData.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${authData.token}`;
      setToken(authData.token);
    }
  };

  const isAdmin = () => {
    return user?.userType === 'admin';
  };

  const isSuperAdmin = () => {
    return user?.userType === 'admin' && user?.isSuperAdmin;
  };

  const hasRepositoryAccess = (repositoryCode = null, accessLevel = 'Read') => {
    if (isAdmin()) return true; // Admins have access to everything
    
    if (!user?.tenants) return false;
    
    if (repositoryCode) {
      const tenant = user.tenants.find(t => t.code === repositoryCode);
      if (!tenant) return false;
      
      const accessLevels = { 'Read': 1, 'Write': 2, 'Admin': 3 };
      return accessLevels[tenant.accessLevel] >= accessLevels[accessLevel];
    }
    
    return user.tenants.length > 0;
  };

  const getCurrentTenant = useCallback(() => {
    return user?.selectedTenant || user?.defaultTenant || null;
  }, [user?.selectedTenant, user?.defaultTenant]);

  // Try to restore/ensure tenant context if missing but token is valid
  const ensureTenantContext = useCallback(async (currentUserParam = null) => {
    try {
      const currentUser = currentUserParam || user;
      if (!currentUser) return false;
      const currentSelection = currentUser.selectedTenant;
      if (currentSelection?.code && currentSelection.code !== 'DEFAULT') {
        localStorage.setItem('selectedTenantCode', currentSelection.code);
        return true;
      }
      const savedCode = localStorage.getItem('selectedTenantCode');
      const availableTenants = currentUser.tenants || [];
      const candidateCode = (
        (savedCode && (availableTenants.find(t => (t.code || t.tenant_code) === savedCode)?.code || availableTenants.find(t => (t.code || t.tenant_code) === savedCode)?.tenant_code)) ||
        (currentUser.defaultTenant?.code && currentUser.defaultTenant.code !== 'DEFAULT' && currentUser.defaultTenant.code) ||
        (currentUser.defaultTenant?.tenant_code && currentUser.defaultTenant.tenant_code !== 'DEFAULT' && currentUser.defaultTenant.tenant_code) ||
        (availableTenants.length === 1 ? (availableTenants[0].code || availableTenants[0].tenant_code) : null)
      );
      if (candidateCode) {
        const result = await selectTenant(candidateCode, { silent: true });
        return !!result?.success;
      }
      return false;
    } catch (e) {
      console.error('ensureTenantContext error:', e);
      return false;
    }
  }, [user, selectTenant]);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      const profileUser = response.data.user;
      const updatedUser = {
        ...profileUser,
        selectedTenant: user?.selectedTenant,
        tenants: profileUser.tenants || user?.tenants,
        defaultTenant: profileUser.defaultTenant || user?.defaultTenant,
        requiresTenantSelection: profileUser.requiresTenantSelection || user?.requiresTenantSelection
      };
      setUser(updatedUser);
      if (!updatedUser.selectedTenant && !updatedUser.defaultTenant) {
        await ensureTenantContext(updatedUser);
      }
      return { success: true };
    } catch (error) {
      console.error('Profile refresh failed:', error);
      return { success: false, error: error.message };
    }
  }, [user?.selectedTenant, user?.tenants, user?.defaultTenant, user?.requiresTenantSelection, ensureTenantContext]);

  const value = {
    user,
    loading,
    token,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    hasRole,
    hasPermission,
    selectTenant,
    selectRole,
    updateAuthData,
    isAdmin,
    isSuperAdmin,
    hasRepositoryAccess,
    getCurrentTenant,
    ensureTenantContext,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 